import { useInfiniteQuery, UseInfiniteQueryOptions } from "@tanstack/react-query";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import shopService from "../services/shopService";
import { IShopCategory, IShopFilters, IShopHistoryItem, IShopItem, IUserBalance } from "../types";

export const useGetShopItems = (
  filters: IShopFilters,
  options?: Partial<UseInfiniteQueryOptions<any, Error>>
) => {
  return useInfiniteQuery<{ data: IShopItem[]; totalPages: number }, Error>({
    queryKey: [
      "shopItems",
      filters.categoryId,
      filters.tokenId,
      filters.sortBy,
      filters.search,
      filters.priceRange?.min,
      filters.priceRange?.max,
    ],
    queryFn: ({ pageParam, signal }) => shopService.getShopItems({ pageParam, filters, signal }),
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      if (nextPage > lastPage.totalPages) return undefined;
      return nextPage;
    },
    initialPageParam: 1,
    retry: 1,
    ...options,
  });
};

export const useGetUserBalance = (options?: Partial<UseQueryOptions<any, Error>>) => {
  return useQuery<IUserBalance[], Error>({
    queryKey: ["userShopBalance"],
    queryFn: () => shopService.getUserBalance(),
    ...options,
  });
};

export const useGetShopCategories = (options?: Partial<UseQueryOptions<any, Error>>) => {
  return useQuery<IShopCategory[], Error>({
    queryKey: ["shopCategories"],
    queryFn: () => shopService.getShopCategories(),
    ...options,
  });
};

export const useGetUserShopItems = (options?: Partial<UseQueryOptions<any, Error>>) => {
  return useQuery<Record<string, IShopHistoryItem[]>, Error>({
    queryKey: ["userShopItems"],
    queryFn: () => shopService.getUserShopItems(),
    ...options,
  });
};

export const usePurchaseShopItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tokenId,
      itemId,
      quantity,
      discountId,
    }: {
      tokenId: number;
      itemId: number;
      quantity?: number;
      discountId?: number;
    }) => shopService.purchaseShopItem({ tokenId, itemId, quantity, discountId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userShopBalance"] });
      queryClient.invalidateQueries({ queryKey: ["userShopItems"] });
      queryClient.invalidateQueries({ queryKey: ["shopItems"] });
    },
  });
};
